import * as C from '../../constants';
import supplierPaymentService from '../../../../services/payment-service/supplier-payment-service';
import downloadService from '../../../../services/download-service';
import filterStorage from '../../../../services/supplier-payments-filter-storage';

export default {
  /**
   * @param commit
   */
  clearState({ commit }) {
    commit(C.SUPPLIER_PAYMENTS_CLEAR);
  },

  /**
   * @param commit
   * @returns {Promise<T | never>}
   */
  getFilterData({ commit }) {
    commit(C.SUPPLIER_PAYMENTS_FILTER_LOAD_PENDING);
    return supplierPaymentService.getFilterData()
      .then((res) => {
        commit(C.SUPPLIER_PAYMENTS_FILTER_LOAD_SUCCESS, res.data);
        // Loading the filters clears the selections, so the remembered ones go back on after it.
        const saved = filterStorage.load();
        if (saved) {
          commit(C.SUPPLIER_PAYMENTS_FILTER_RESTORE, saved);
        }
      })
      .catch(() => {
        commit(C.SUPPLIER_PAYMENTS_FILTER_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  downloadList({ commit, state, dispatch }) {
    commit(C.SUPPLIER_PAYMENTS_LIST_DOWNLOAD_PENDING);
    switch (state.filter.data.selectedTableType) {
      case 1:
        dispatch('downloadPaidSupplierPayments');
        break;
      case 2:
        dispatch('downloadDueSupplierPayments');
        break;
      default:
        dispatch('downloadSupplierPayments');
        break;
    }
  },

  /**
   * @param commit
   * @param state
   */
  downloadPaidSupplierPayments({ commit, state }) {
    commit(C.SUPPLIER_PAYMENTS_LIST_DOWNLOAD_PENDING);
    supplierPaymentService.downloadPaidSupplierPayments(
      state.filter.lastSearch.date,
      state.filter.lastSearch.data,
    ).then((res) => {
      commit(C.SUPPLIER_PAYMENTS_LIST_DOWNLOAD_SUCCESS);
      downloadService.downloadCsv('supplier_payments_paid', res.data);
    }).catch(() => {
      commit(C.SUPPLIER_PAYMENTS_LIST_DOWNLOAD_FAILURE);
    });
  },

  /**
   * @param commit
   * @param state
   */
  downloadDueSupplierPayments({ commit, state }) {
    commit(C.SUPPLIER_PAYMENTS_LIST_DOWNLOAD_PENDING);
    supplierPaymentService.downloadDueSupplierPayments(
      state.filter.lastSearch.date,
      state.filter.lastSearch.data,
    ).then((res) => {
      commit(C.SUPPLIER_PAYMENTS_LIST_DOWNLOAD_SUCCESS);
      downloadService.downloadCsv('supplier_payments_due', res.data);
    }).catch(() => {
      commit(C.SUPPLIER_PAYMENTS_LIST_DOWNLOAD_FAILURE);
    });
  },

  /**
   * @param commit
   * @param state
   */
  downloadSupplierPayments({ commit, state }) {
    commit(C.SUPPLIER_PAYMENTS_LIST_DOWNLOAD_PENDING);
    supplierPaymentService.downloadSupplierPayments(
      state.filter.lastSearch.date,
      state.filter.lastSearch.data,
    ).then((res) => {
      commit(C.SUPPLIER_PAYMENTS_LIST_DOWNLOAD_SUCCESS);
      downloadService.downloadCsv('supplier_payments', res.data);
    }).catch(() => {
      commit(C.SUPPLIER_PAYMENTS_LIST_DOWNLOAD_FAILURE);
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  downloadSpecifications({ state, dispatch }, payload) {
    switch (state.filter.data.selectedTableType) {
      case 1:
        dispatch('downloadPaidSupplierPaymentSpecifications', payload);
        break;
      case 2:
        dispatch('downloadDueSupplierPaymentSpecifications', payload);
        break;
      default:
        dispatch('downloadSupplierPaymentSpecifications', payload);
        break;
    }
  },

  /**
   * @param commit
   * @param state
   * @param supplier
   */
  downloadPaidSupplierPaymentSpecifications({ commit, state }, supplier) {
    commit(C.SUPPLIER_PAYMENTS_SPEC_DOWNLOAD_PENDING, supplier.name);
    supplierPaymentService.downloadPaidSupplierPaymentSpecifications(
      state.filter.lastSearch.date,
      state.filter.lastSearch.data,
      supplier.id,
    ).then((res) => {
      commit(C.SUPPLIER_PAYMENTS_SPEC_DOWNLOAD_SUCCESS);
      const supplierName = supplier.name.toLowerCase().replace(/ /g, '_');
      const name = `supplier_payments_paid_specifications_${supplierName}`;
      downloadService.downloadCsv(name, res.data);
    }).catch(() => {
      commit(C.SUPPLIER_PAYMENTS_SPEC_DOWNLOAD_FAILURE);
    });
  },

  /**
   * @param commit
   * @param state
   * @param supplier
   */
  downloadDueSupplierPaymentSpecifications({ commit, state }, supplier) {
    commit(C.SUPPLIER_PAYMENTS_SPEC_DOWNLOAD_PENDING, supplier.name);
    supplierPaymentService.downloadDueSupplierPaymentSpecifications(
      state.filter.lastSearch.date,
      state.filter.lastSearch.data,
      supplier.id,
    ).then((res) => {
      commit(C.SUPPLIER_PAYMENTS_SPEC_DOWNLOAD_SUCCESS);
      const supplierName = supplier.name.toLowerCase().replace(/ /g, '_');
      const name = `supplier_payments_due_specifications_${supplierName}`;
      downloadService.downloadCsv(name, res.data);
    }).catch(() => {
      commit(C.SUPPLIER_PAYMENTS_SPEC_DOWNLOAD_FAILURE);
    });
  },

  /**
   * @param commit
   * @param state
   * @param payment
   */
  downloadSupplierPaymentSpecifications({ commit, state }, payment) {
    commit(C.SUPPLIER_PAYMENTS_SPEC_DOWNLOAD_PENDING, payment.id);
    supplierPaymentService.downloadSupplierPaymentSpecifications(
      state.filter.lastSearch.date,
      state.filter.lastSearch.data,
      payment.id,
    ).then((res) => {
      commit(C.SUPPLIER_PAYMENTS_SPEC_DOWNLOAD_SUCCESS);
      const name = `supplier_payments_specifications_${payment.id}`;
      downloadService.downloadCsv(name, res.data);
    }).catch(() => {
      commit(C.SUPPLIER_PAYMENTS_SPEC_DOWNLOAD_FAILURE);
    });
  },

  /**
   * @param dispatch
   * @param type
   */
  loadTableData({ dispatch }, type) {
    switch (type) {
      case 1:
        dispatch('loadPaidSupplierPayments');
        break;
      case 2:
        dispatch('loadDueSupplierPayments');
        break;
      case 3:
        dispatch('loadSupplierPayments');
        break;
      default:
        break;
    }
  },

  /**
   * @param commit
   * @param state
   * @returns {Promise<T | never>}
   */
  loadPaidSupplierPayments({ commit, state }) {
    commit(C.SUPPLIER_PAYMENTS_LIST_LOAD_PENDING);
    return supplierPaymentService.getPaidSupplierPayments(
      state.filter.date,
      state.filter.data,
    )
      .then((res) => {
        commit(C.SUPPLIER_PAYMENTS_LIST_LOAD_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.SUPPLIER_PAYMENTS_LIST_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @param state
   * @returns {Promise<T | never>}
   */
  loadDueSupplierPayments({ commit, state }) {
    commit(C.SUPPLIER_PAYMENTS_LIST_LOAD_PENDING);
    return supplierPaymentService.getDueSupplierPayments(
      state.filter.date,
      state.filter.data,
    )
      .then((res) => {
        commit(C.SUPPLIER_PAYMENTS_LIST_LOAD_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.SUPPLIER_PAYMENTS_LIST_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @param state
   * @returns {Promise<T | never>}
   */
  loadSupplierPayments({ commit, state }) {
    commit(C.SUPPLIER_PAYMENTS_LIST_LOAD_PENDING);
    return supplierPaymentService.getSupplierPayments(
      state.filter.date,
      state.filter.data,
    )
      .then((res) => {
        commit(C.SUPPLIER_PAYMENTS_LIST_LOAD_SUCCESS, { suppliers: res.data });
      })
      .catch(() => {
        commit(C.SUPPLIER_PAYMENTS_LIST_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @param radio
   */
  updateFilterActiveTable({ commit }, radio) {
    commit(C.SUPPLIER_PAYMENTS_FILTER_TYPE_CHANGE, radio);
  },

  /**
   * @param commit
   * @param state
   * @param arrivalFromDate
   */
  updateFilterArrivalFromDate({ commit, state }, arrivalFromDate) {
    commit(C.SUPPLIER_PAYMENTS_FILTER_ARRIVAL_FROM_CHANGE, arrivalFromDate);
    const arrivalToDate = state.filter.date.arrivalToDate;
    if (arrivalFromDate > arrivalToDate) {
      commit(C.SUPPLIER_PAYMENTS_FILTER_ARRIVAL_TO_CHANGE, arrivalFromDate);
    }
  },

  /**
   * @param commit
   * @param arrivalToDate
   */
  updateFilterArrivalToDate({ commit }, arrivalToDate) {
    commit(C.SUPPLIER_PAYMENTS_FILTER_ARRIVAL_TO_CHANGE, arrivalToDate);
  },

  /**
   * @param commit
   * @param state
   * @param paidDateFromDate
   */
  updateFilterPaidDateFromDate({ commit, state }, paidDateFromDate) {
    commit(C.SUPPLIER_PAYMENTS_FILTER_PAID_FROM_CHANGE, paidDateFromDate);
    const paidDateToDate = state.filter.date.paidDateToDate;
    if (paidDateFromDate > paidDateToDate) {
      commit(C.SUPPLIER_PAYMENTS_FILTER_PAID_TO_CHANGE, paidDateFromDate);
    }
  },

  /**
   * @param commit
   * @param paidDateToDate
   */
  updateFilterPaidDateToDate({ commit }, paidDateToDate) {
    commit(C.SUPPLIER_PAYMENTS_FILTER_PAID_TO_CHANGE, paidDateToDate);
  },

  /**
   * @param commit
   * @param productTypes
   */
  updateSelectedProductTypes({ commit }, productTypes) {
    commit(C.SUPPLIER_PAYMENTS_FILTER_PRODUCTS_CHANGE, productTypes);
  },

  /**
   * @param commit
   * @param brands
   */
  updateSelectedBrands({ commit }, brands) {
    commit(C.SUPPLIER_PAYMENTS_FILTER_BRANDS_CHANGE, brands);
  },

  /**
   * @param commit
   * @param suppliers
   */
  updateSelectedSuppliers({ commit }, suppliers) {
    commit(C.SUPPLIER_PAYMENTS_FILTER_SUPPLIERS_CHANGE, suppliers);
  },

  /**
   * @param commit
   * @param domains
   */
  updateSelectedDomains({ commit }, domains) {
    commit(C.SUPPLIER_PAYMENTS_FILTER_DOMAINS_CHANGE, domains);
  },

  /**
   * @param commit
   * @param search
   */
  updateSupplierSearch({ commit }, search) {
    commit(C.SUPPLIER_PAYMENTS_FILTER_SUPPLIERS_SEARCH, search);
  },

  /**
   * @param commit
   * @param hideZeroSuppliers
   */
  updateHideZeroSuppliers({ commit }, hideZeroSuppliers) {
    commit(C.SUPPLIER_PAYMENTS_FILTER_HIDE_ZERO_CHANGE, hideZeroSuppliers);
  },

  /**
   * Forget the remembered filters and go back to the defaults.
   *
   * @param commit
   */
  resetFilters({ commit }) {
    filterStorage.clear();
    commit(C.SUPPLIER_PAYMENTS_FILTER_RESET);
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/payments/supplierPayments/actions.js